import {tokens} from '@pleo-io/telescope'
import * as ReachAccordion from '@reach/accordion'
import {motion} from 'framer-motion'
import React, {ReactNode} from 'react'
import styled from 'styled-components'

import minusIcon from '@/images/icons/minus.svg'
import plusIcon from '@/images/icons/plus.svg'
import {Text} from '@helios/shared/styles/typography'
import {colors} from '@helios/shared/theme'
import {durationStringToNumber} from '@helios/shared/utils/duration-string-to-number'
import mq from '@helios/shared/utils/media-query'

export type Props = {
    collapsible?: boolean
    onChange?: (index: number) => void
    children: ReactNode
}

export const Accordion = ({children, collapsible, onChange}: Props) => (
    <ReachAccordion.Accordion collapsible={collapsible} onChange={onChange}>
        {React.Children.map(children, (child, i) => (
            <AccordionItemWrapper index={i}>{child}</AccordionItemWrapper>
        ))}
    </ReachAccordion.Accordion>
)

type AccordionItemProps = {
    title: string
    children: ReactNode
}

export const AccordionItem = ({children, title}: AccordionItemProps) => {
    const {isExpanded, index} = ReachAccordion.useAccordionItemContext()

    return (
        <>
            <Text as="h3" variant="paragraph-large">
                <AccordionButton>
                    <AccordionButtonInner>
                        {title}

                        <Icon src={isExpanded ? minusIcon : plusIcon} alt="" aria-hidden />
                    </AccordionButtonInner>
                </AccordionButton>
            </Text>

            <motion.div
                initial={isExpanded ? 'active' : 'inactive'}
                variants={variants}
                animate={isExpanded ? 'active' : 'inactive'}
                key={index}
                transition={{
                    type: 'tween',
                    duration: durationStringToNumber(tokens.slow) / 1000,
                }}
            >
                <AccordionPanel>{children}</AccordionPanel>
            </motion.div>
        </>
    )
}

const variants = {
    active: {
        opacity: 1,
        height: 'auto',
        overflow: 'hidden',
        display: 'block',
    },
    inactive: {
        opacity: 0,
        height: 0,
        overflow: 'hidden',
        transitionEnd: {
            display: 'none',
        },
    },
}

const AccordionItemWrapper = styled(ReachAccordion.AccordionItem)`
    border-bottom: 1px solid ${colors.border.secondary};
`

const AccordionButton = styled(ReachAccordion.AccordionButton)`
    width: 100%;
    padding: unset;
    padding-top: ${tokens.spacing12};
    padding-bottom: ${tokens.spacing12};
    text-align: unset;
    cursor: pointer;
    background: unset;
    border: unset;

    ${mq.min('sm')} {
        padding-top: ${tokens.spacing16};
        padding-bottom: ${tokens.spacing16};
    }
`

const AccordionButtonInner = styled.span`
    display: grid;
    justify-content: space-between;
    grid-template-columns: 1fr auto;
    width: 100%;
    align-items: center;
    column-gap: ${tokens.spacing12};

    ${mq.min('sm')} {
        column-gap: ${tokens.spacing20};
    }
`

const AccordionPanel = styled(ReachAccordion.AccordionPanel)`
    display: block;
    padding-bottom: ${tokens.spacing12};

    ${mq.min('sm')} {
        padding-bottom: ${tokens.spacing16};
    }
`

const Icon = styled.img`
    width: 24px;
    height: 24px;
    object-fit: contain;

    ${mq.min('sm')} {
        width: 32px;
        height: 32px;
    }
`
